import { Box, Typography } from '@mui/material';
import moment from 'moment';

import FHTooltip from '../../../components/base/FHTooltip';
import {
  FORTA_BLUE,
  FORTA_SUCCESS,
  FORTA_SUCCESS_2,
  FORTA_YELLOW,
  FORTA_YELLOW_2,
} from '../../../utils/colors';
import { getUtcOffset } from '../../../utils/common';
import { getEvidencedBasedInterventionsData } from './constants';
import {
  GoalTypes,
  TargetStages,
  TPGoalDataCollectionMethod,
} from '../../../components/layout/treatment-plan/goals/common/constants/general';
import { CQAReviewStatus, SessionEvalSchemaVersion } from '../../../components/layout/qa/constants';
import { BTSessionStatus, BAYS } from '../../../constants';
import { PROCEDURE_DURATION_TO_UNIT_FACTOR } from '../../../pages/constants';
import {
  SESSION_BREAK_MAX_DURATION_SECONDS,
  SESSION_BREAK_MIN_DURATION_SECONDS,
} from './constants';

import type { TFunction } from 'i18next';
import type {
  IBTSession,
  IClinicalQAReview,
  IClinicalQAReviewDiscourse,
  IEvidenceBasedInterventionsUsed,
  IOption,
  ISessionGoalsAndTrials,
  ISessionMinified,
  ISessionPracticedTargetTrial,
  ITreatmentPlanGoal,
} from '../../../utils/types';
import type { Moment } from 'moment';

const validateTrial = (
  trial: ISessionPracticedTargetTrial,
  dataCollectionMethod: TPGoalDataCollectionMethod | null,
) => {
  if (
    (dataCollectionMethod === TPGoalDataCollectionMethod.PERCENTAGE && !trial.attempts) ||
    (dataCollectionMethod === TPGoalDataCollectionMethod.INSTANCES &&
      trial.target?.is_event_duration_required &&
      !trial.instances)
  ) {
    return false;
  }
  return true;
};

export const getInvalidTrialList = (goalsPracticedList: ISessionGoalsAndTrials | null) => {
  const invalidTrialList = [];

  if (!goalsPracticedList) return [];

  for (const id in goalsPracticedList) {
    const trials = goalsPracticedList[id]?.trials;
    if (trials) {
      invalidTrialList.push(
        ...trials.filter(
          (trial) => !validateTrial(trial, goalsPracticedList[id]?.data_collection_method ?? null),
        ),
      );
    }
  }

  return invalidTrialList;
};

export const getInvalidGoalList = (
  goalsPracticedList: ISessionGoalsAndTrials,
  treatmentPlanGoals: ITreatmentPlanGoal[],
) => {
  const invalidGoalList = [];

  if (!goalsPracticedList) return [];
  for (const id in goalsPracticedList) {
    const trials = goalsPracticedList[id]?.trials;
    if (!trials || trials.length === 0) {
      const invalidGoal = treatmentPlanGoals.find(
        (goal: ITreatmentPlanGoal) => goal?.id === Number(id),
      );
      if (invalidGoal) {
        invalidGoalList.push(invalidGoal);
      }
    }
  }
  return invalidGoalList;
};

export const getInvalidGoalTechniqueList = (
  goalsPracticedList: ISessionGoalsAndTrials,
  treatmentPlanGoals: ITreatmentPlanGoal[],
) => {
  const invalidGoalList: ITreatmentPlanGoal[] = [];

  if (!goalsPracticedList) return [];
  for (const id in goalsPracticedList) {
    if ((goalsPracticedList[id]?.techniques?.length ?? 0) === 0) {
      const invalidGoal = treatmentPlanGoals.find(
        (goal: ITreatmentPlanGoal) => goal?.id === Number(id),
      );
      const isVisible = invalidGoal?.is_active || (goalsPracticedList[id]?.trials.length ?? 0) > 0;
      if (invalidGoal && isVisible) {
        invalidGoalList.push(invalidGoal);
      }
    }
  }
  return invalidGoalList;
};

export const getInvalidGoalTimingList = (
  goalsPracticedList: ISessionGoalsAndTrials,
  treatmentPlanGoals: ITreatmentPlanGoal[],
  expectedDuration: number | undefined,
) => {
  const invalidGoalList: ITreatmentPlanGoal[] = [];

  if (!goalsPracticedList) return [];
  for (const id in goalsPracticedList) {
    const invalidGoal = treatmentPlanGoals.find(
      (goal: ITreatmentPlanGoal) => goal?.id === Number(id),
    );
    const isVisible = invalidGoal?.is_active || (goalsPracticedList[id]?.trials.length ?? 0) > 0;
    if (!invalidGoal || !isVisible || invalidGoal.goal_type !== GoalTypes.SkillAcquisition) {
      continue;
    }

    const timings = goalsPracticedList[id]?.timings ?? [];
    const hasInvalidTiming =
      timings.length === 0 ||
      timings.some((timing) => {
        const startOffset = timing.start_offset_seconds;
        const endOffset = timing.end_offset_seconds;

        return (
          startOffset == null ||
          endOffset == null ||
          expectedDuration == null ||
          startOffset < 0 ||
          endOffset > expectedDuration ||
          endOffset <= startOffset
        );
      });

    if (hasInvalidTiming) {
      invalidGoalList.push(invalidGoal);
    }
  }
  return invalidGoalList;
};

export const getSelectedTechniqueOptions = (
  t: TFunction<'sessionNotes'>,
  evidenceBasedInterventionsUsed: IEvidenceBasedInterventionsUsed,
): IOption[] => {
  const selectedTechniqueOptions: IOption[] = getEvidencedBasedInterventionsData(t)
    .filter(
      (intervention) =>
        evidenceBasedInterventionsUsed?.[intervention.id],
    )
    .map((intervention) => ({
      id: intervention.id,
      label: intervention.label,
    }));

  if (evidenceBasedInterventionsUsed?.other) {
    selectedTechniqueOptions.push({
      id: 'other',
      label: t('evidenceBasedInterventionsUsed.fields.other'),
    });
  }

  return selectedTechniqueOptions;
};

export const validateCheckBoxFields = (values: any) => {
  if (
    hasNoFieldChecked(values, 'signs_and_symptoms_observed') ||
    hasNoFieldChecked(values, 'evidence_based_interventions_used')
  ) {
    return false;
  }
  return true;
};

export const hasNoFieldChecked = (values: any, field: string) => {
  return Object.keys(values[field]).filter((key) => values[field][key]).length === 0;
};

export const getSessionNarrativeLength = (expected_duration: number) => {
  return expected_duration >= 14400
    ? 420
    : expected_duration >= 10800
      ? 300
      : expected_duration >= 7200
        ? 240
        : 180;
};

export const getRoundedBreakDurationSeconds = (durationSeconds: number) => {
  if (durationSeconds <= 0) return 0;
  return (
    Math.ceil(durationSeconds / PROCEDURE_DURATION_TO_UNIT_FACTOR) *
    PROCEDURE_DURATION_TO_UNIT_FACTOR
  );
};

export const getSessionBreakUnits = (durationSeconds: number) =>
  getRoundedBreakDurationSeconds(durationSeconds) / PROCEDURE_DURATION_TO_UNIT_FACTOR;

export const getSessionUnitsAfterBreaks = (
  expectedDuration: number,
  breaks: Array<{ start_offset_seconds: number; end_offset_seconds: number }>,
) => {
  const roundedBreakSeconds = breaks.reduce(
    (total, sessionBreak) =>
      total +
      getRoundedBreakDurationSeconds(
        sessionBreak.end_offset_seconds - sessionBreak.start_offset_seconds,
      ),
    0,
  );

  return Math.max(0, (expectedDuration - roundedBreakSeconds) / PROCEDURE_DURATION_TO_UNIT_FACTOR);
};

export const getSessionOffsetSeconds = (sessionStart: Moment, value: Moment) =>
  Math.max(0, value.diff(sessionStart, 'seconds'));

export const getSessionBreakDateTime = (
  sessionStart: Moment,
  sessionEnd: Moment,
  value: Moment,
) => {
  const candidate = sessionStart
    .clone()
    .hour(value.hour())
    .minute(value.minute())
    .second(0)
    .millisecond(0);

  if (candidate.isBefore(sessionStart) && sessionEnd.isAfter(sessionStart, 'day')) {
    return candidate.add(1, 'day');
  }

  return candidate;
};

export const getSessionBreakValidationMessage = (
  sessionStart: Moment,
  sessionEnd: Moment,
  breakStart: Moment,
  breakEnd: Moment,
) => {
  if (breakStart.isBefore(sessionStart)) {
    return 'Break time cannot be before the session start time';
  }
  if (breakEnd.isAfter(sessionEnd)) {
    return 'Break time cannot be after the session end time';
  }
  if (breakEnd.isSameOrBefore(breakStart)) {
    return 'Break end time must be after break start time';
  }

  const durationSeconds = breakEnd.diff(breakStart, 'seconds');
  if (durationSeconds <= SESSION_BREAK_MIN_DURATION_SECONDS) {
    return 'Break duration must be greater than 7 minutes';
  }
  if (durationSeconds >= SESSION_BREAK_MAX_DURATION_SECONDS) {
    return 'Break duration must be less than 22 minutes';
  }

  return null;
};

export const getSessionSignatureStatusAndWarnings = (
  t: TFunction<'sessionNotes'>,
  session: IBTSession | null,
) => {
  const btString = session?.bay === BAYS.BAY_3 ? 'BT' : 'VBT';
  if (!session) return null;
  const { guardian_provider_signature, status, date_provider_signed } = session;

  let isSigned =
    session?.status === BTSessionStatus.Completed ||
    session?.status === BTSessionStatus.AwaitingReview;

  if (
    session.status === BTSessionStatus.Completed &&
    session?.clinical_qa_review?.status !== CQAReviewStatus.Acknowledged
  ) {
    isSigned = false;
  }

  let signatory = t('signed');
  let showParentSignatureWarning = false;

  if (session && !isSigned) {
    const isVbtContext = session.bay !== BAYS.BAY_3;
    const providerSignedGuardianPending =
      isVbtContext && Boolean(date_provider_signed) && !guardian_provider_signature;
    const awaitingGuardianBay26 =
      status === BTSessionStatus.AwaitingGuardianSignature && session.bay === BAYS.BAY_2_6;
    const showGuardianAsSignatory = awaitingGuardianBay26 || providerSignedGuardianPending;

    signatory = showGuardianAsSignatory ? t('guardian') : btString;
  }

  if (date_provider_signed && !guardian_provider_signature) {
    showParentSignatureWarning =
      moment().diff(moment(date_provider_signed), 'hours') > 4 && !isSigned;
  }

  return {
    isSigned,
    signatory,
    showParentSignatureWarning,
  };
};

export const getGuardianSignatureWarningVisibility = (session: ISessionMinified | null) => {
  if (!session) return null;
  const { status, date_provider_signed } = session;

  if (status === BTSessionStatus.AwaitingGuardianSignature && date_provider_signed) {
    return moment().diff(moment(date_provider_signed), 'hours') > 4;
  }
  return false;
};

export const sortTrialsByTargetOrder = (trials: ISessionPracticedTargetTrial[]) => {
  if (!trials) return [];
  return [...trials].sort((a, b) => (a.target?.order ?? 0) - (b.target?.order ?? 0));
};

export const formatDurationTrialTime = (seconds: number) => {
  const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return (
    <Box sx={{ display: 'flex' }}>
      <Typography fontWeight={700} textAlign="right" width={24}>
        {hrs}
      </Typography>
      <Typography fontSize={12} fontWeight={700} mt={0.2}>
        h
      </Typography>
      <Typography mx={0.5}> : </Typography>
      <Typography fontWeight={700} textAlign="right" width={24}>
        {mins}
      </Typography>
      <Typography fontSize={12} fontWeight={700} mt={0.2}>
        m
      </Typography>
      <Typography mx={0.5}> : </Typography>
      <Typography fontWeight={700} textAlign="right" width={24}>
        {secs}
      </Typography>
      <Typography fontSize={12} fontWeight={700} mt={0.2}>
        s
      </Typography>
    </Box>
  );
};

export const shouldDisplayTargetStage = (goalType: GoalTypes, stage?: TargetStages | null) => {
  if (!stage) return false;

  if (goalType === GoalTypes.SkillAcquisition) {
    return [
      TargetStages.Acquisition,
      TargetStages.Generalization,
      TargetStages.Maintenance,
    ].includes(stage);
  }

  return (
    goalType === GoalTypes.BehaviorReduction &&
    [TargetStages.InProgress, TargetStages.Maintenance].includes(stage)
  );
};

export const generateCreateTrialPayload = (
  trial: ISessionPracticedTargetTrial,
  dataCollectionMethod: TPGoalDataCollectionMethod | null,
) => {
  return dataCollectionMethod === TPGoalDataCollectionMethod.PERCENTAGE
    ? {
        target_id: trial.target?.id,
        date_measured: moment(new Date()).toISOString(),
        utc_offset: getUtcOffset(moment(new Date())),
        attempts: trial.attempts,
        expected_response: trial.expected_response,
        occurrence: null,
      }
    : dataCollectionMethod === TPGoalDataCollectionMethod.RATE
      ? {
          target_id: trial.target?.id,
          date_measured: moment(new Date()).toISOString(),
          utc_offset: getUtcOffset(moment(new Date())),
          rate_events: trial?.rate_events,
        }
      : dataCollectionMethod === TPGoalDataCollectionMethod.DURATION
        ? {
            target_id: trial.target?.id,
            date_measured: moment(new Date()).toISOString(),
            utc_offset: getUtcOffset(moment(new Date())),
            duration_events: trial?.duration_events,
          }
        : // If instance type without durations
          dataCollectionMethod === TPGoalDataCollectionMethod.INSTANCES &&
            !trial.target?.is_event_duration_required
          ? {
              target_id: trial.target?.id,
              date_measured: moment(new Date()).toISOString(),
              utc_offset: getUtcOffset(moment(new Date())),
              attempts: null,
              expected_response: null,
              occurrence: trial.occurrence,
            }
          : // if instance type with duration
            dataCollectionMethod === TPGoalDataCollectionMethod.INSTANCES &&
            trial.target?.is_event_duration_required && {
              target_id: trial.target?.id,
              date_measured: moment(new Date()).toISOString(),
              utc_offset: getUtcOffset(moment(new Date())),
              attempts: null,
              expected_response: null,
              instances: trial.instances,
              occurrence: trial.occurrence,
            };
};

export const getCQAReviewPayload = (
  reviewData: IClinicalQAReview,
  gradingVersion: SessionEvalSchemaVersion,
  latestComment?: IClinicalQAReviewDiscourse,
) => {
  const data: any = {
    id: reviewData.id,
    is_datetime_accurate: reviewData.is_datetime_accurate,
    is_language_objective: reviewData.is_language_objective,
    is_note_submitted_within_48hours_of_session:
      reviewData.is_note_submitted_within_48hours_of_session,
    has_10data_points_per_hour: reviewData.has_10data_points_per_hour,
    is_at_least_2programs_run_per_hour: reviewData.is_at_least_2programs_run_per_hour,
    is_all_active_programs_used_within_7days: reviewData.is_all_active_programs_used_within_7days,
    is_detail_sufficient_quality: reviewData.is_detail_sufficient_quality,
    is_overall_coherent: reviewData.is_overall_coherent,
    comment: latestComment?.comment === '' ? null : latestComment?.comment,
    is_edit_requested: latestComment?.is_edit_requested,
  };
  if (gradingVersion === SessionEvalSchemaVersion.V2) {
    data.one_setting_per_session_note = reviewData.one_setting_per_session_note;
    data.therapist_role_aligns_with_tp_goals = reviewData.therapist_role_aligns_with_tp_goals;
  } else {
    data.is_data_collected_per_forta_protocols = reviewData.is_data_collected_per_forta_protocols;
    data.is_detail_sufficient_quantity = reviewData.is_detail_sufficient_quantity;
  }
  return data;
};

export const goalChipRenderer = (
  goal: ITreatmentPlanGoal | any,
  isLinkedBehaviorReductionGoal: boolean,
) => (
  <FHTooltip
    arrow
    title={
      goal?.goal_type === GoalTypes.BehaviorReduction
        ? isLinkedBehaviorReductionGoal
          ? 'Linked Behavior Reduction Goal'
          : 'Behavior Reduction Goal'
        : isLinkedBehaviorReductionGoal
          ? 'Linked Skill Acquisition Goal'
          : 'Skill Acquisition Goal'
    }
    sx={{
      borderRadius: '8px',
    }}
    tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
    tooltipbackground={FORTA_BLUE.light3}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        padding: '4px 8px',
        borderRadius: '8px',
        border: `1px solid ${
          goal?.goal_type === GoalTypes.BehaviorReduction ? FORTA_YELLOW : FORTA_SUCCESS
        }`,
        backgroundColor:
          goal?.goal_type === GoalTypes.BehaviorReduction ? FORTA_YELLOW_2 : FORTA_SUCCESS_2,
      }}
    >
      <Typography color={FORTA_BLUE.primary} fontWeight={700} sx={{ px: 1 }}>
        {goal?.goal_type === GoalTypes.BehaviorReduction ? 'BRG' : 'SAG'}
      </Typography>
    </Box>
  </FHTooltip>
);
